import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useParams } from "react-router-dom";
import { filterParam } from "modules/main/services/endpoints";
import { ProductCatalog } from "modules/catalog/components/productCatalog";
import { FilterBlock } from "modules/catalog/components/filterBlock";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { CatalogResponse } from 'modules/catalog/types/catalogResponse';
import { endPointCatalog, endPointMenu} from "modules/main/services/endpoints";
import { AppMenuOptions, AppSubMenu } from 'modules/main/types/menus';
import { useFetch } from "modules/main/hooks/useFetch";
import {FetchDataOptions} from 'modules/main/services/fetchData';
import { UserData } from 'modules/authentication/types/user-data';
import { Select, SelectContent, SelectItem,SelectTrigger,SelectValue } from '@/components/ui/select';
import { ArrowDownNarrowWide, ArrowDownWideNarrow} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { getVisitorId } from 'utils/analytics';
import { SearchBar } from "modules/main/components/searchBar";
import { PageBreadcrumbs } from "modules/main/components/page-breadcrumbs";
import type { PageBreadcrumb } from "modules/main/components/page-breadcrumbs";
import { usePageTitle } from "hooks/usePageTitle";

/**
 * Formats a section slug into a human-readable header title.
 * URL-decodes the slug (defense-in-depth: react-router already decodes
 * params), replaces `_` and `-` with spaces, and Title Cases each word.
 * Falls back to the raw slug (separators replaced, trimmed) on URIError.
 */
const formatSectionTitle = (section: string): string => {
    try {
        return decodeURIComponent(section)
            .replace(/[_-]/g, " ")
            .trim()
            .split(/\s+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    } catch {
        return section.replace(/[_-]/g, " ").trim();
    }
};

/**
 * Resolves the canonical "Category > Subcategory" breadcrumb for a section
 * slug against the GET /api/sections hierarchy (AppMenuOptions[]).
 * A top-level category slug yields the category; a subcategory slug yields
 * the category plus its subcategory; anything else (unknown slug or menu
 * still loading) yields null so the builder falls back to formatSectionTitle
 * and the header stays populated.
 */
type SectionBreadcrumb =
  | { category: AppMenuOptions }
  | { category: AppMenuOptions; subcategory: AppSubMenu }
  | null;
// invariant: subcategory set ⇒ category set (sub found within category.sub_menu)
const resolveSectionBreadcrumb = (
    section: string,
    menu: AppMenuOptions[] | null | undefined
): SectionBreadcrumb => {
    if (section && menu) {
        const category = menu.find((item) => item.slug === section);
        if (category) return { category };
        for (const item of menu) {
            const sub = item.sub_menu?.find((s) => s.slug === section);
            if (sub) return { category: item, subcategory: sub };
        }
    }
    return null;
};

/**
 * Builds the crumbs for a section page and renders them through the shared
 * PageBreadcrumbs component. Section breadcrumbs resolve to 3 crumbs (Inicio
 * → /store, Catálogo → /catalog, tail) when the slug is a top-level category,
 * unknown, or the menu is still loading, and 4 crumbs on subcategory pages
 * (…, parent category → /catalog/{slug}). PageBreadcrumbs collapses any
 * breadcrumb with more than MAX_VISIBLE_CRUMBS crumbs into first + ellipsis
 * dropdown + last. The tail is the subcategory name, the category name, or
 * the formatted section title when the slug is unknown or the menu is still
 * loading. Pure: same inputs, same JSX.
 */
const buildSectionBreadcrumb = (
    section: string,
    menu: AppMenuOptions[] | null | undefined
): ReactNode => {
    const resolved = resolveSectionBreadcrumb(section, menu);

    let lastCrumb: string;
    if (resolved && "subcategory" in resolved) {
        lastCrumb = resolved.subcategory.name;
    } else if (resolved?.category) {
        lastCrumb = resolved.category.name;
    } else {
        lastCrumb = formatSectionTitle(section);
    }

    const crumbs: PageBreadcrumb[] = [
        { label: "Inicio", to: "/store" },
        { label: "Catálogo", to: "/catalog" },
    ];
    // "subcategory" in resolved is the TS 4.9 narrowing idiom: subcategory
    // set ⇒ category set too.
    if (resolved && "subcategory" in resolved) {
        crumbs.push({
            label: resolved.category.name,
            to: `/catalog/${resolved.category.slug}`,
        });
    }
    crumbs.push({ label: lastCrumb });

    return <PageBreadcrumbs crumbs={crumbs} />;
};

const Catalog = () => {    
    const isDesktop = useMediaQuery(isDesktopSize);
    const location = useLocation();
    const path_params = useParams();    
    const params = new URLSearchParams(location.search);
    const section  = path_params.section ?? "";    
    const { setBreadcrumb, setShowBack } = usePageTitle();
    const { data: menuData } = useFetch<AppMenuOptions[]>(endPointMenu);

    // The breadcrumb replaces the h2 title on section pages (HeaderBar
    // precedence). JSX is built inside the effect — building it in render
    // would create a new element identity every render and re-trigger this
    // effect through the context re-render (infinite loop).
    // EXTENSION 8: also defensively reset the global back arrow so the stale
    // ArrowLeft left by a previous route never shows on breadcrumb routes.
    useEffect(() => {
        setBreadcrumb(buildSectionBreadcrumb(section, menuData));
        setShowBack(false);
        return () => setBreadcrumb(null);
    }, [section, menuData, setBreadcrumb, setShowBack]);    

    const currentPage = params.get("page") ? params.get("page") : "";
    const brand_filters:filterParam = {
            name:"brand",
            value:params.getAll("brand") ? params.getAll("brand") : []
    }
    const tag_filters:filterParam = {
        name:"tag",
        value:params.getAll("tag") ? params.getAll("tag") : []
    }    
    
    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{
            "x-visitor-id": getVisitorId()
        },
        body:undefined
    }

    const userData = JSON.parse(localStorage.getItem("userData")??"{}") as UserData;   
    const [sortBy,setSortBy] = useState("several_options");
    const {data,error,loading} = useFetch<CatalogResponse>(endPointCatalog(section,String(currentPage),sortBy,[brand_filters,tag_filters]),requestOptions);
    return(
    <>
    <main className="py-2 2xl:py-4 px-4 2xl:px-16 flex flex-col gap-2 2xl:gap-4">        
        <div className="flex justify-center w-full">
            <SearchBar className="w-full max-w-xl" />
        </div>
        {/* <AspectRatio ratio={ (isDesktop)?30 / 3:20 / 5}>
            <img className="Image" src="https://t3.ftcdn.net/jpg/03/98/03/96/360_F_398039682_a9khCuqZkrjuGT8l7N5A7TUxn6VjjHgA.jpg" alt=""/>
        </AspectRatio> */}
        {
        (!isDesktop)&&(
        <div className="flex flex-row justify-between">
            <div className="flex gap-2 items-center">
                <FilterBlock isSearch={false} isDesktop={isDesktop} section={section} initialFilters={[brand_filters,tag_filters]}
                    data={data?.filters??[]}/>
                
                <span className="font-bold text-xs">Filtrar</span>        
            </div>        
            <div className='flex gap-2 py-2 items-center'>
                <Select value={sortBy} onValueChange={(value)=>{setSortBy(value)}}>
                    <SelectTrigger className='w-16 text-muted-foreground'>
                        <SelectValue>
                            {
                            (sortBy === "lower_price") ? <ArrowDownNarrowWide className="w-4 h-4"/>:
                                <ArrowDownWideNarrow className="w-4 h-4"/>
                            } 
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent id="sortby">
                        <SelectItem value='lower_price'>
                            <div className='flex gap-2'><ArrowDownNarrowWide className="w-4 h-4"/>Menor Precio</div>
                        </SelectItem>
                        <SelectItem value='higher_price'>
                            <div className='flex gap-2'><ArrowDownWideNarrow className="w-4 h-4"/>Mayor Precio</div>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>            
        </div> 
        )
        } 
        <div className="flex flex-row gap-4">
            {
            (isDesktop)&&(
            <div className="px-2 min-w-52" >                
                <FilterBlock isSearch={false} isDesktop={isDesktop} section={section} initialFilters={[brand_filters,tag_filters]}                
                data={data?.filters??[]}/>
            </div>
            )
            }
            <div className='flex flex-1 flex-col w-full gap-2'>
            { (isDesktop)&&               
                (<div className='flex w-full justify-between align-middle px-8'>
                    <h3 className="pt-2 text-sm"><span className="text-muted-foreground font-bold">PRODUCTOS</span></h3>
                    <div className='flex gap-2 py-2 items-center'> 
                        <Label className='text-xs text-muted-foreground' htmlFor="sortby">Orden</Label>
                        <Select value={sortBy} onValueChange={(value)=>{setSortBy(value)}}>
                            <SelectTrigger className='w-[18em] text-muted-foreground'>
                                <SelectValue placeholder="Ordenar Por"/>
                            </SelectTrigger>
                            <SelectContent id="sortby">                         
                                <SelectItem value='lower_price'>
                                    <div className='flex gap-2'><ArrowDownNarrowWide className="w-4 h-4"/>Menor Precio</div>
                                </SelectItem>
                                <SelectItem value='higher_price'>
                                    <div className='flex gap-2'><ArrowDownWideNarrow className="w-4 h-4"/>Mayor Precio</div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>)      
            }
                <ProductCatalog 
                    section={section}
                    brand_filters={brand_filters}
                    tag_filters={tag_filters}
                    currentPage={currentPage}
                    search_params={location.search}
                    isMobile={!isDesktop}
                    data={data ?? {} as CatalogResponse}
                    error={error}
                    loading={loading}
                    />     
            </div>        
        </div>
    </main>
    </>
    );
    
}


export default Catalog;
